import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthService, TokenPair } from '../auth/auth.service';

/**
 * Interceptor de autenticación:
 *   - Añade Bearer token a cada petición DEL API (`environment.apiUrl`).
 *   - Maneja 401 del API con refresh automático (una sola llamada simultánea).
 *   - Si el refresh falla, cierra la sesión y redirige a /login.
 *
 * Fuera del API no interviene: los assets estáticos (bucket GCS tras el LB)
 * no entienden nuestro Bearer, y un 401 transitorio del bucket disparaba el
 * ciclo 401→refresh→retry en bucle — rotaba la cookie httpOnly hasta que dos
 * refresh chocaban (INVALID_REFRESH) y tumbaba una sesión válida.
 */
@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private readonly auth = inject(AuthService);

  private isRefreshing = false;
  private readonly refreshToken$ = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.apiUrl)) return next.handle(req);

    const token = this.auth.getAccessToken();
    const authReq = token ? this.addToken(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !req.url.includes('/auth/')) {
          return this.handle401(req, next);
        }
        return throwError(() => err);
      }),
    );
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshToken$.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token))),
      );
    }

    this.isRefreshing = true;
    this.refreshToken$.next(null);

    return this.auth.refresh().pipe(
      switchMap((res: TokenPair) => {
        this.isRefreshing = false;
        this.refreshToken$.next(res.access_token);
        return next.handle(this.addToken(req, res.access_token));
      }),
      catchError((err: HttpErrorResponse) => {
        this.isRefreshing = false;
        this.auth.logout();
        return throwError(() => err);
      }),
    );
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
